// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { Card, Icon, ListItem, Separator, useScreenSize } from '@alto/design-system';
import { InstructionalVideo, useQueryInstructionalVideos } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';

const MAX_SLIDES = 1.5;
const SLIDE_WIDTH = 825;
// For mobile web, we want to make sure the first video is fully visible
const MAX_SLICES_FOR_SMALL_SCREEN = 1;
const SLIDE_WIDTH_FOR_SMALL_SCREEN = 340;
const MAX_VIDEOS = 5;

// corrects styles because Swiper is inside Card flexbox/grid container
// https://github.com/nolimits4web/swiper/issues/3599#issuecomment-953430779
const SwiperContainer = styled.div`
  width: 100%;
  max-width: 100%;
  max-height: 100vh;
  // CSS Grid/Flexbox bug size workaround
  // @see https://github.com/kenwheeler/slick/issues/982
  min-height: 0;
  min-width: 0;
`;

export const InjectionGuides = () => {
  const { navigate } = useNavigation();
  const { videos } = useQueryInstructionalVideos();
  const { isSMScreenOrBigger } = useScreenSize();
  const { trackEvent } = useAnalytics();
  const shouldShowInjectionGuides = !!videos && videos.length > 0;

  const handleViewAllVideos = () => {
    navigate('RouteInstructionalVideos');
  };

  useEffect(() => {
    if (!shouldShowInjectionGuides) return;

    trackEvent({
      event: EVENTS.INJECTIONS_GUIDES_VIEWED,
      params: {
        origin: 'homescreen',
      },
    });
  }, [trackEvent, shouldShowInjectionGuides]);

  if (!shouldShowInjectionGuides) {
    return null;
  }

  return (
    <Card
      illustrationSrc={getImage('syringe')}
      illustrationAccessibilityLabel={IMG_ALT_TEXTS.syringe}
      title="Injection guides"
    >
      <SwiperContainer>
        <Swiper
          navigation={true}
          modules={[Navigation]}
          slidesPerView={isSMScreenOrBigger ? MAX_SLIDES : MAX_SLICES_FOR_SMALL_SCREEN}
          width={isSMScreenOrBigger ? SLIDE_WIDTH : SLIDE_WIDTH_FOR_SMALL_SCREEN}
          style={
            {
              '--swiper-navigation-color': COLORS.PALETTE.GREYSCALE.DEFAULT,
            } as React.CSSProperties
          }
        >
          {videos.slice(0, MAX_VIDEOS).map((video, index) => {
            return (
              <SwiperSlide key={video.id}>
                <InstructionalVideo
                  origin="Homescreen"
                  index={index}
                  video={video}
                  isInCarousel
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </SwiperContainer>
      <Separator />
      <ListItem
        title="View all videos"
        onPress={handleViewAllVideos}
        RightContent={
          <Icon
            name="chevronright-small"
            type="grey"
          />
        }
      />
    </Card>
  );
};
